<template>
  <div class="reset-password-page">
    <div class="reset-card">
      <div v-if="!resetComplete">
        <h2>Reset Your Password</h2>
        <p class="subtitle">Enter your new password below</p>
        
        <form @submit.prevent="handleResetPassword">
          <div class="form-group">
            <label>New Password *</label>
            <input
              type="password"
              v-model="newPassword"
              placeholder="Enter new password (min 6 characters)"
              :disabled="loading"
            />
          </div>
          
          <div class="form-group">
            <label>Confirm Password *</label>
            <input
              type="password"
              v-model="confirmPassword"
              placeholder="Confirm your new password"
              :disabled="loading"
            />
          </div>
          
          <el-alert
            v-if="error"
            :title="error"
            type="error"
            :closable="true"
            @close="error = ''"
            style="margin-bottom: 20px;">
          </el-alert>
          
          <button type="submit" class="reset-button" :disabled="loading">
            {{ loading ? 'Resetting...' : 'Reset Password' }}
          </button>
        </form>
        
        <div class="back-link">
          <a @click="goToHome">Back to Home</a>
        </div>
      </div>
      
      <div v-else class="success-message">
        <div class="icon-container">
          <i class="el-icon-success success-icon"></i>
        </div>
        <h2>Password Reset Successful!</h2>
        <p>Your password has been reset successfully. You can now log in with your new password.</p>
        <el-button type="primary" @click="goToLogin">
          Go to Login
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'ResetPassword',
  data() {
    return {
      newPassword: '',
      confirmPassword: '',
      loading: false,
      error: '',
      resetComplete: false,
      token: ''
    }
  },
  mounted() {
    this.token = this.$route.query.token
    
    if (!this.token) {
      this.error = 'Invalid reset link. Please request a new password reset.'
    }
  },
  methods: {
    async handleResetPassword() {
      // Validation
      if (!this.newPassword || !this.confirmPassword) {
        this.error = 'Please fill in all fields'
        return
      }
      
      if (this.newPassword.length < 6) {
        this.error = 'Password must be at least 6 characters'
        return
      }
      
      if (this.newPassword !== this.confirmPassword) {
        this.error = 'Passwords do not match'
        return
      }
      
      if (!this.token) {
        this.error = 'Invalid reset link'
        return
      }
      
      this.loading = true
      this.error = ''
      
      try {
        const response = await axios.post('/api/user/reset-password', {
          token: this.token,
          new_password: this.newPassword
        })
        
        if (response && response.data && response.data.code === 200) {
          this.resetComplete = true
          this.$message.success('Password reset successfully!')
        } else {
          this.error = response.data.error || 'Failed to reset password'
        }
      } catch (error) {
        console.error('Reset password error:', error)
        this.error = error.response?.data?.error || 'Failed to reset password. The link may have expired.'
      } finally {
        this.loading = false
      }
    },
    goToLogin() {
      this.$router.push('/')
    },
    goToHome() {
      this.$router.push('/')
    }
  }
}
</script>

<style scoped>
.reset-password-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
}

.reset-card {
  background: white;
  border-radius: 12px;
  padding: 50px 40px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 500px;
  width: 100%;
}

h2 {
  font-size: 28px;
  color: #333;
  margin-bottom: 10px;
  font-weight: 600;
  text-align: center;
}

.subtitle {
  font-size: 14px;
  color: #666;
  margin-bottom: 30px;
  text-align: center;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
}

.form-group input {
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.3s;
  box-sizing: border-box;
}

.form-group input:focus {
  outline: none;
  border-color: #667eea;
}

.form-group input:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

.reset-button {
  width: 100%;
  padding: 14px 30px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  margin-top: 10px;
}

.reset-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
}

.reset-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.back-link {
  text-align: center;
  margin-top: 25px;
}

.back-link a {
  color: #667eea;
  text-decoration: none;
  font-size: 14px;
  cursor: pointer;
}

.back-link a:hover {
  text-decoration: underline;
}

.success-message {
  text-align: center;
}

.icon-container {
  margin-bottom: 25px;
}

.success-icon {
  font-size: 80px;
  color: #67C23A;
}

.success-message p {
  font-size: 16px;
  color: #666;
  line-height: 1.6;
  margin-bottom: 30px;
}

@media (max-width: 600px) {
  .reset-card {
    padding: 40px 30px;
  }
  
  h2 {
    font-size: 24px;
  }
}
</style>

